import React, { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { MAX_END_DATE } from '~/consts/luxLoyalty';

import { type CustomRule, type MatchField, updateCustomRule } from '~/services/LuxLoyalty';

import ApiError from '~/types/ApiError';

import MatchConfigBuilder from './MatchConfigBuilder';
import { parseCustomRuleApiErrors } from './customRuleApiErrors';

interface Props {
  themeColor: string;
  open: boolean;
  onClose: (updatedRule?: CustomRule | null) => void;
  rule: CustomRule | null;
}

export default function ReadOnlyCustomRuleDialog({ themeColor, open, onClose, rule }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [endDate, setEndDate] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (rule && rule.endDate) {
      setEndDate(dayjs(rule.endDate).subtract(1, 'day').format('YYYY-MM-DD'));
    } else {
      setEndDate(null);
    }
  }, [rule]);

  const updateMutation = useMutation({
    mutationFn: (updateData: { id: string; data: Parameters<typeof updateCustomRule>[1] }) =>
      updateCustomRule(updateData.id, updateData.data),
    onSuccess: (response) => {
      enqueueSnackbar('End date updated successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'custom-rules'] });
      setValidationErrors({});
      // Pass the updated rule back to the parent component
      const updatedRule = response?.result?.updatedRule || null;
      handleClose(updatedRule);
    },
    onError: (error: ApiError) => {
      if (error.name === 'ValidationError' && error.status === 400) {
        const { errors, hadUnparsed } = parseCustomRuleApiErrors(error.errors);
        setValidationErrors(errors);
        const hasFieldErrors = Object.keys(errors).length > 0;
        if (hasFieldErrors && !hadUnparsed) {
          enqueueSnackbar('Please fix validation errors', { variant: 'error' });
        } else {
          enqueueSnackbar(error.message || 'Validation error', { variant: 'error' });
        }
      } else {
        enqueueSnackbar(`Error updating end date: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    },
  });

  const handleClose = (updatedRule?: CustomRule | null): void => {
    setValidationErrors({});
    onClose(updatedRule);
  };

  const handleSubmit = (): void => {
    if (!rule) {
      return;
    }

    if (!endDate) {
      enqueueSnackbar('Please provide an end date', { variant: 'error' });
      return;
    }

    const minimalUpdateData = {
      type: rule.type,
      endDate: dayjs.tz(endDate, 'Australia/Sydney').add(1, 'day').startOf('day').toISOString(),
    };

    updateMutation.mutate({ id: rule.id, data: minimalUpdateData });
  };

  const handleCopy = (value: string | number | null | undefined, label: string): void => {
    const textToCopy = value?.toString() || '';
    navigator.clipboard.writeText(textToCopy);
    enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' });
  };

  if (!rule) {
    return null;
  }

  let identifierType = 'None';
  let identifierValue = '';

  if (rule.offerId) {
    identifierType = 'Offer ID';
    identifierValue = rule.offerId;
  } else if (rule.packageId) {
    identifierType = 'Package ID';
    identifierValue = rule.packageId;
  } else if (rule.ratePlanId) {
    identifierType = 'Rate Plan ID';
    identifierValue = rule.ratePlanId;
  } else if (rule.earnKey) {
    identifierType = 'Earn Key';
    identifierValue = rule.earnKey;
  } else if (rule.promoKey) {
    identifierType = 'Promo Key';
    identifierValue = rule.promoKey;
  } else if (rule.productType) {
    identifierType = 'Product Type';
    identifierValue = rule.productType;
  }

  // The exact identifier anchor field, used to warn when matchConfig targets it.
  const anchorField: MatchField | undefined = rule.offerId
    ? 'offerId'
    : rule.packageId
      ? 'packageId'
      : rule.earnKey
        ? 'earnKey'
        : rule.promoKey
          ? 'promoKey'
          : rule.productType
            ? 'productType'
            : undefined;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>View Custom Rule (Read-Only)</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.contrastText">
            This rule has already started and cannot be fully edited. You can only update the end date.
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Stack spacing={3}>
            {/* ID Field */}
            <TextField
              label="ID"
              value={rule.id}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleCopy(rule.id, 'ID')} edge="end" size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Name Field */}
            <TextField
              label="Name"
              value={rule.name}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleCopy(rule.name, 'Name')} edge="end" size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Type and Purpose */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Type"
                value={rule.type}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopy(rule.type, 'Type')} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Purpose"
                value={rule.purpose}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopy(rule.purpose, 'Purpose')} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* Points Type */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Points Type"
                value={rule.pointsType}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopy(rule.pointsType, 'Points Type')} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* Identifier Type */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Identifier Type"
                value={identifierType}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopy(identifierType, 'Identifier Type')} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* Identifier Value */}
            {identifierValue && (
              <TextField
                label={identifierType}
                value={identifierValue}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopy(identifierValue, identifierType)} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Display-only matchConfig for rules that have one. */}
            {rule.matchConfig && (
              <MatchConfigBuilder
                readOnly
                value={rule.matchConfig}
                onChange={() => undefined}
                anchorField={anchorField}
              />
            )}

            {/* Points configuration - only show relevant field based on type */}
            {(rule.type === 'BONUS_POINTS_ONLY' || rule.type === 'BONUS_POINTS') && (
              <TextField
                label="Points Value"
                value={rule.pointsValue ?? ''}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopy(rule.pointsValue, 'Points Value')} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {rule.type === 'MULTIPLE_ON_BASE_POINTS' && (
              <TextField
                label="Points Multiplier"
                value={rule.pointsMultiplier ?? ''}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleCopy(rule.pointsMultiplier, 'Points Multiplier')}
                        edge="end"
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Date fields */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                value={rule.startDate ? dayjs(rule.startDate).format('YYYY-MM-DD') : ''}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          handleCopy(rule.startDate ? dayjs(rule.startDate).format('YYYY-MM-DD') : '', 'Start Date')
                        }
                        edge="end"
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* End date - EDITABLE */}
              <TextField
                label="End Date (Editable)"
                type="date"
                value={endDate || ''}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  if (validationErrors.endDate) {
                    setValidationErrors({ ...validationErrors, endDate: '' });
                  }
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: MAX_END_DATE,
                }}
                error={!!validationErrors.endDate}
                helperText={validationErrors.endDate || 'Only this field can be modified'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderColor: themeColor,
                    '&.Mui-focused fieldset': {
                      borderColor: themeColor,
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button sx={{ color: themeColor, borderColor: themeColor }} onClick={() => handleClose()} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateMutation.isPending}
          sx={{
            bgcolor: themeColor,
            '&:hover': {
              bgcolor: themeColor,
              opacity: 0.9,
            },
          }}
        >
          {updateMutation.isPending ? 'Updating...' : 'Update End Date'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
